import React from 'react';
import { ExternalLink, RotateCcw } from 'lucide-react';
import { usePageEditor } from '../components/PageContent/usePageEditor';
import {
  Section, TextField, NumberField, TextAreaField, SelectField, CheckboxField,
  ImageField, RepeatableList, StringList, ICON_OPTIONS, B2B_GRADIENTS,
} from '../components/PageContent/Fields';
import '../components/Common/Common.css';
import '../components/PageContent/PageContent.css';

const STOREFRONT_URL = (process.env.REACT_APP_STOREFRONT_URL || 'https://tech360.ma').replace(/\/+$/, '');

function B2BPageContent() {
  const {
    content, setSection, replaceSection, loading, saving, resetting,
    message, error, setError, updatedAt, save, reset,
  } = usePageEditor('b2b');

  if (loading) {
    return <div className="loading">Chargement du contenu…</div>;
  }

  if (!content) {
    return (
      <div className="page-header">
        <h1>Page « Espace B2B »</h1>
        <p className="pc-alert pc-alert-error">{error || 'Contenu indisponible.'}</p>
      </div>
    );
  }

  const hero = content.hero || {};
  const solutions = content.solutions || {};
  const installation = content.installation || {};
  const fleetTypes = content.fleet_types || {};
  const steps = content.steps || {};
  const fuel = content.fuel || {};
  const whyUs = content.why_us || {};
  const contact = content.contact || {};
  const finalCta = content.final_cta || {};

  const setInstallationImage = (index, url) => {
    const images = [...(installation.images || [])];
    images[index] = url;
    setSection('installation', { images });
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Page « Espace B2B »</h1>
          <p className="pc-status">
            Modifiez le contenu affiché sur {STOREFRONT_URL}/b2b.
            {updatedAt ? ` Dernière modification : ${new Date(updatedAt).toLocaleString('fr-FR')}.` : ' Contenu d\'origine.'}
          </p>
        </div>
        <div className="pc-toolbar">
          <a
            href={`${STOREFRONT_URL}/b2b`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-neutral"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <ExternalLink size={15} /> Voir la page
          </a>
          <button
            type="button"
            className="btn btn-neutral"
            onClick={reset}
            disabled={resetting || saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <RotateCcw size={15} /> {resetting ? 'Restauration…' : "Restaurer l'original"}
          </button>
        </div>
      </div>

      {message && <div className="pc-alert pc-alert-success">{message}</div>}
      {error && <div className="pc-alert pc-alert-error">{error}</div>}

      {/* ---------------------------------------------------------- Hero */}
      <Section
        title="Bannière principale"
        description="La vidéo de fond se règle dans Paramètres société ; l'image ci-dessous sert de repli si aucune vidéo n'est chargée."
      >
        <div className="form-row">
          <TextField
            label="Badge (petit texte au-dessus du titre)"
            value={hero.badge}
            onChange={(v) => setSection('hero', { badge: v })}
          />
          <TextField
            label="Texte du bouton principal"
            value={hero.cta_label}
            onChange={(v) => setSection('hero', { cta_label: v })}
          />
        </div>
        <div className="form-row">
          <TextField
            label="Titre"
            value={hero.title}
            onChange={(v) => setSection('hero', { title: v })}
          />
          <TextField
            label="Titre — partie colorée"
            value={hero.title_highlight}
            onChange={(v) => setSection('hero', { title_highlight: v })}
            hint="Affichée en dégradé bleu/vert à la suite du titre"
          />
        </div>
        <TextAreaField
          label="Sous-titre"
          value={hero.subtitle}
          onChange={(v) => setSection('hero', { subtitle: v })}
          rows={3}
        />
        <TextField
          label="Message WhatsApp pré-rempli"
          value={hero.whatsapp_message}
          onChange={(v) => setSection('hero', { whatsapp_message: v })}
        />
        <ImageField
          label="Image de repli (si aucune vidéo)"
          value={hero.fallback_image}
          onChange={(v) => setSection('hero', { fallback_image: v })}
          onError={setError}
        />
        <StringList
          label="Garanties affichées sous les boutons"
          items={hero.trust_badges}
          onChange={(v) => setSection('hero', { trust_badges: v })}
          addLabel="Ajouter une garantie"
          placeholder="ex: Support 7j/7"
        />
        <div className="form-row">
          <TextField
            label="Encadré vert — titre"
            value={hero.card_note_title}
            onChange={(v) => setSection('hero', { card_note_title: v })}
          />
          <TextField
            label="Encadré vert — texte"
            value={hero.card_note_text}
            onChange={(v) => setSection('hero', { card_note_text: v })}
          />
        </div>
      </Section>

      {/* --------------------------------------------------------- Stats */}
      <Section
        title="Chiffres de la bannière"
        description="Les compteurs animés affichés dans l'encadré à droite de la bannière."
      >
        <RepeatableList
          items={content.stats}
          onChange={(items) => replaceSection('stats', items)}
          itemLabel="Chiffre"
          addLabel="Ajouter un chiffre"
          newItem={() => ({ value: 0, suffix: '+', label: '' })}
          renderItem={(item, update) => (
            <div className="form-row" style={{ marginBottom: 0 }}>
              <NumberField
                label="Valeur (nombre)"
                value={item.value}
                onChange={(v) => update({ value: v })}
              />
              <TextField
                label="Suffixe"
                value={item.suffix}
                onChange={(v) => update({ suffix: v })}
                placeholder="+ ou %"
              />
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
            </div>
          )}
        />
      </Section>

      {/* ----------------------------------------------------- Solutions */}
      <Section title="Nos solutions" description="Les grands blocs image + texte alternés.">
        <div className="form-row">
          <TextField
            label="Surtitre"
            value={solutions.eyebrow}
            onChange={(v) => setSection('solutions', { eyebrow: v })}
          />
          <TextField
            label="Titre"
            value={solutions.title}
            onChange={(v) => setSection('solutions', { title: v })}
          />
        </div>
        <TextAreaField
          label="Sous-titre"
          value={solutions.subtitle}
          onChange={(v) => setSection('solutions', { subtitle: v })}
          rows={2}
        />
        <RepeatableList
          items={solutions.items}
          onChange={(items) => setSection('solutions', { items })}
          itemLabel="Solution"
          addLabel="Ajouter une solution"
          newItem={() => ({ icon: 'eye', title: '', description: '', image: '', features: [], gradient: 'blue' })}
          renderItem={(item, update) => (
            <>
              <div className="form-row">
                <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
                <SelectField
                  label="Icône"
                  value={item.icon}
                  onChange={(v) => update({ icon: v })}
                  options={ICON_OPTIONS}
                />
                <SelectField
                  label="Couleur"
                  value={item.gradient}
                  onChange={(v) => update({ gradient: v })}
                  options={B2B_GRADIENTS}
                />
              </div>
              <TextAreaField
                label="Description"
                value={item.description}
                onChange={(v) => update({ description: v })}
                rows={3}
              />
              <ImageField
                label="Image"
                value={item.image}
                onChange={(v) => update({ image: v })}
                onError={setError}
                hint="Format conseillé : 16/10 (ex. 1280×800)"
              />
              <StringList
                label="Points forts"
                items={item.features}
                onChange={(v) => update({ features: v })}
                addLabel="Ajouter un point fort"
                hint="Affichés sur deux colonnes — un nombre pair rend mieux"
              />
            </>
          )}
        />
      </Section>

      {/* -------------------------------------------------- Installation */}
      <Section
        title="Installation professionnelle"
        description="Le bloc sombre avec la mosaïque de 4 photos."
      >
        <div className="form-row">
          <TextField
            label="Surtitre"
            value={installation.eyebrow}
            onChange={(v) => setSection('installation', { eyebrow: v })}
          />
          <TextField
            label="Texte du bouton"
            value={installation.cta_label}
            onChange={(v) => setSection('installation', { cta_label: v })}
          />
        </div>
        <div className="form-row">
          <TextField
            label="Titre"
            value={installation.title}
            onChange={(v) => setSection('installation', { title: v })}
          />
          <TextField
            label="Titre — partie colorée"
            value={installation.title_highlight}
            onChange={(v) => setSection('installation', { title_highlight: v })}
          />
        </div>
        <TextAreaField
          label="Description"
          value={installation.description}
          onChange={(v) => setSection('installation', { description: v })}
          rows={3}
        />
        <StringList
          label="Points de la liste"
          items={installation.points}
          onChange={(v) => setSection('installation', { points: v })}
          addLabel="Ajouter un point"
        />
        <TextField
          label="Badge flottant sous les photos"
          value={installation.badge_text}
          onChange={(v) => setSection('installation', { badge_text: v })}
          placeholder="ex: +15 000 installations réalisées"
        />
        <div className="form-row">
          {[0, 1, 2, 3].map((i) => (
            <ImageField
              key={i}
              label={`Photo ${i + 1}`}
              value={(installation.images || [])[i]}
              onChange={(v) => setInstallationImage(i, v)}
              onError={setError}
            />
          ))}
        </div>
      </Section>

      {/* --------------------------------------------------- Fleet types */}
      <Section title="Types de flottes" description="Les trois cartes de segments clients.">
        <div className="form-row">
          <TextField
            label="Surtitre"
            value={fleetTypes.eyebrow}
            onChange={(v) => setSection('fleet_types', { eyebrow: v })}
          />
          <TextField
            label="Titre"
            value={fleetTypes.title}
            onChange={(v) => setSection('fleet_types', { title: v })}
          />
        </div>
        <TextAreaField
          label="Sous-titre"
          value={fleetTypes.subtitle}
          onChange={(v) => setSection('fleet_types', { subtitle: v })}
          rows={2}
        />
        <RepeatableList
          items={fleetTypes.items}
          onChange={(items) => setSection('fleet_types', { items })}
          itemLabel="Type de flotte"
          addLabel="Ajouter un type de flotte"
          newItem={() => ({ image: '', title: '', desc: '', tags: [] })}
          renderItem={(item, update) => (
            <>
              <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
              <TextAreaField
                label="Description"
                value={item.desc}
                onChange={(v) => update({ desc: v })}
                rows={2}
              />
              <ImageField
                label="Image"
                value={item.image}
                onChange={(v) => update({ image: v })}
                onError={setError}
              />
              <StringList
                label="Étiquettes"
                items={item.tags}
                onChange={(v) => update({ tags: v })}
                addLabel="Ajouter une étiquette"
                placeholder="ex: Transport"
                hint="3 étiquettes maximum pour rester lisible"
              />
            </>
          )}
        />
      </Section>

      {/* --------------------------------------------------------- Steps */}
      <Section title="Comment ça marche" description="Les étapes numérotées du processus.">
        <div className="form-row">
          <TextField
            label="Surtitre"
            value={steps.eyebrow}
            onChange={(v) => setSection('steps', { eyebrow: v })}
          />
          <TextField
            label="Titre"
            value={steps.title}
            onChange={(v) => setSection('steps', { title: v })}
          />
        </div>
        <RepeatableList
          items={steps.items}
          onChange={(items) => setSection('steps', { items })}
          itemLabel="Étape"
          addLabel="Ajouter une étape"
          newItem={() => ({ step: '', title: '', description: '' })}
          renderItem={(item, update) => (
            <>
              <div className="form-row">
                <TextField
                  label="Numéro"
                  value={item.step}
                  onChange={(v) => update({ step: v })}
                  placeholder="ex: 05"
                />
                <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
              </div>
              <TextAreaField
                label="Description"
                value={item.description}
                onChange={(v) => update({ description: v })}
                rows={2}
              />
            </>
          )}
        />
      </Section>

      {/* ---------------------------------------------------------- Fuel */}
      <Section title="Bandeau économies carburant" description="Le bandeau vert sur photo de fond.">
        <CheckboxField
          label="Afficher ce bandeau sur le site"
          checked={fuel.enabled !== false}
          onChange={(v) => setSection('fuel', { enabled: v })}
        />
        <div className="form-row">
          <TextField
            label="Badge"
            value={fuel.badge}
            onChange={(v) => setSection('fuel', { badge: v })}
          />
          <TextField
            label="Titre"
            value={fuel.title}
            onChange={(v) => setSection('fuel', { title: v })}
          />
          <TextField
            label="Titre — partie colorée"
            value={fuel.title_highlight}
            onChange={(v) => setSection('fuel', { title_highlight: v })}
          />
        </div>
        <TextAreaField
          label="Description"
          value={fuel.description}
          onChange={(v) => setSection('fuel', { description: v })}
          rows={3}
        />
        <div className="form-row">
          <ImageField
            label="Image de fond"
            value={fuel.background_image}
            onChange={(v) => setSection('fuel', { background_image: v })}
            onError={setError}
          />
          <ImageField
            label="Image latérale"
            value={fuel.side_image}
            onChange={(v) => setSection('fuel', { side_image: v })}
            onError={setError}
          />
        </div>
        <RepeatableList
          items={fuel.stats}
          onChange={(items) => setSection('fuel', { stats: items })}
          itemLabel="Chiffre"
          addLabel="Ajouter un chiffre"
          newItem={() => ({ value: '', label: '' })}
          renderItem={(item, update) => (
            <div className="form-row" style={{ marginBottom: 0 }}>
              <TextField
                label="Valeur"
                value={item.value}
                onChange={(v) => update({ value: v })}
                placeholder="ex: 25%"
              />
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
            </div>
          )}
        />
      </Section>

      {/* -------------------------------------------------------- Why us */}
      <Section title="Pourquoi nous choisir" description="La liste d'arguments et l'encadré sombre « Nos chiffres ».">
        <div className="form-row">
          <TextField
            label="Surtitre"
            value={whyUs.eyebrow}
            onChange={(v) => setSection('why_us', { eyebrow: v })}
          />
          <TextField
            label="Titre"
            value={whyUs.title}
            onChange={(v) => setSection('why_us', { title: v })}
          />
        </div>
        <TextAreaField
          label="Description"
          value={whyUs.description}
          onChange={(v) => setSection('why_us', { description: v })}
          rows={2}
        />
        <RepeatableList
          items={whyUs.items}
          onChange={(items) => setSection('why_us', { items })}
          itemLabel="Argument"
          addLabel="Ajouter un argument"
          newItem={() => ({ icon: 'check', text: '' })}
          renderItem={(item, update) => (
            <div className="form-row" style={{ marginBottom: 0 }}>
              <SelectField
                label="Icône"
                value={item.icon}
                onChange={(v) => update({ icon: v })}
                options={ICON_OPTIONS}
              />
              <TextField label="Texte" value={item.text} onChange={(v) => update({ text: v })} />
            </div>
          )}
        />

        <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #e2e8f0' }} />

        <div className="form-row">
          <TextField
            label="Encadré — titre"
            value={whyUs.card_title}
            onChange={(v) => setSection('why_us', { card_title: v })}
          />
          <TextField
            label="Encadré — sous-titre"
            value={whyUs.card_subtitle}
            onChange={(v) => setSection('why_us', { card_subtitle: v })}
          />
        </div>
        <RepeatableList
          items={whyUs.card_stats}
          onChange={(items) => setSection('why_us', { card_stats: items })}
          itemLabel="Chiffre"
          addLabel="Ajouter un chiffre"
          newItem={() => ({ value: 0, suffix: '+', label: '', bar: 80 })}
          renderItem={(item, update) => (
            <div className="form-row" style={{ marginBottom: 0 }}>
              <NumberField
                label="Valeur (nombre)"
                value={item.value}
                onChange={(v) => update({ value: v })}
              />
              <TextField
                label="Suffixe"
                value={item.suffix}
                onChange={(v) => update({ suffix: v })}
                placeholder="+ ou %"
              />
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
              <NumberField
                label="Barre de progression (%)"
                value={item.bar}
                onChange={(v) => update({ bar: v })}
                min={0}
                max={100}
              />
            </div>
          )}
        />
      </Section>

      {/* ------------------------------------------------------- Contact */}
      <Section
        title="Section contact"
        description="Textes autour du formulaire de demande. Téléphone, e-mail et adresse proviennent des Paramètres société."
      >
        <div className="form-row">
          <TextField
            label="Surtitre"
            value={contact.eyebrow}
            onChange={(v) => setSection('contact', { eyebrow: v })}
          />
          <TextField
            label="Titre"
            value={contact.title}
            onChange={(v) => setSection('contact', { title: v })}
          />
        </div>
        <TextAreaField
          label="Sous-titre"
          value={contact.subtitle}
          onChange={(v) => setSection('contact', { subtitle: v })}
          rows={2}
        />
        <div className="form-row">
          <TextField
            label="Note sous le téléphone"
            value={contact.phone_note}
            onChange={(v) => setSection('contact', { phone_note: v })}
          />
          <TextField
            label="Note sous l'e-mail"
            value={contact.email_note}
            onChange={(v) => setSection('contact', { email_note: v })}
          />
          <TextField
            label="Note sous l'adresse"
            value={contact.address_note}
            onChange={(v) => setSection('contact', { address_note: v })}
          />
        </div>
        <TextField
          label="Message WhatsApp pré-rempli"
          value={contact.whatsapp_message}
          onChange={(v) => setSection('contact', { whatsapp_message: v })}
        />
        <div className="form-row">
          <TextField
            label="Texte du bouton d'envoi"
            value={contact.submit_label}
            onChange={(v) => setSection('contact', { submit_label: v })}
          />
          <TextField
            label="Mention sous le formulaire"
            value={contact.legal_note}
            onChange={(v) => setSection('contact', { legal_note: v })}
          />
        </div>
      </Section>

      {/* ----------------------------------------------------- Final CTA */}
      <Section title="Appel à l'action final" description="Le bloc sombre tout en bas de la page.">
        <div className="form-row">
          <TextField
            label="Badge"
            value={finalCta.badge}
            onChange={(v) => setSection('final_cta', { badge: v })}
          />
          <TextField
            label="Titre"
            value={finalCta.title}
            onChange={(v) => setSection('final_cta', { title: v })}
          />
          <TextField
            label="Titre — partie colorée"
            value={finalCta.title_highlight}
            onChange={(v) => setSection('final_cta', { title_highlight: v })}
          />
        </div>
        <TextAreaField
          label="Sous-titre"
          value={finalCta.subtitle}
          onChange={(v) => setSection('final_cta', { subtitle: v })}
          rows={2}
        />
        <ImageField
          label="Image de fond"
          value={finalCta.background_image}
          onChange={(v) => setSection('final_cta', { background_image: v })}
          onError={setError}
        />
        <div className="form-row">
          <TextField
            label="Bouton principal — texte"
            value={finalCta.primary_label}
            onChange={(v) => setSection('final_cta', { primary_label: v })}
            hint="Renvoie vers le formulaire de la page"
          />
          <TextField
            label="Bouton secondaire — texte"
            value={finalCta.secondary_label}
            onChange={(v) => setSection('final_cta', { secondary_label: v })}
          />
          <TextField
            label="Bouton secondaire — lien"
            value={finalCta.secondary_url}
            onChange={(v) => setSection('final_cta', { secondary_url: v })}
            placeholder="/products"
          />
        </div>
      </Section>

      <div className="pc-save-bar">
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
}

export default B2BPageContent;
